import { forwardRef, type TextareaHTMLAttributes, useId } from 'react';
import { cn } from './lib/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hideLabel?: boolean;
  hint?: string;
  error?: string;
  /**
   * `default`  — bordered field.
   * `composer` — borderless growing input for the post/chat composer.
   */
  variant?: 'default' | 'composer';
  /** Grows with content up to this many rows, then scrolls. */
  maxRows?: number;
  containerClassName?: string;
}

const VARIANT = {
  default:
    'rounded-lg border border-input bg-card px-3 py-2.5 focus:border-primary focus:ring-2 focus:ring-ring/25',
  composer: 'rounded-none border-0 bg-transparent p-0 focus:ring-0',
} as const;

const DEFAULT_MAX_ROWS = 8;
const LINE_HEIGHT_REM = 1.375;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label, hideLabel = false, hint, error, variant = 'default',
    maxRows = DEFAULT_MAX_ROWS, rows = 1, className, containerClassName, id, ...props
  },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

  return (
    <div className={cn('w-full', containerClassName)}>
      {label ? (
        <label
          htmlFor={fieldId}
          className={cn('mb-1.5 block text-base font-medium text-secondary-foreground', hideLabel && 'sr-only')}
        >
          {label}
        </label>
      ) : null}

      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={describedBy}
        style={{ maxHeight: `${maxRows * LINE_HEIGHT_REM}rem` }}
        className={cn(
          'w-full resize-none text-md text-foreground outline-none transition-colors duration-fast placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          VARIANT[variant],
          error && 'border-destructive focus:border-destructive focus:ring-destructive/25',
          className,
        )}
        {...props}
      />

      {error ? (
        <p id={`${fieldId}-error`} className="mt-1.5 text-sm font-medium text-destructive">{error}</p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="mt-1.5 text-sm text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
});
